import { useEffect, useRef } from 'react';
import type { GeoJSONSource, Map as MapboxMap } from 'mapbox-gl';
import { useMapStore } from '../../../stores/mapStore';
import { API_URL } from '../../../lib/env';
import { getAuthBearerHeaders } from '../../../services/api';

export const LIVE_TRAFFIC_SOURCE_ID = 'coordal-live-traffic';
export const LIVE_TRAFFIC_LAYER_ID = 'coordal-live-traffic-lines';

const CONGESTION_COLOR: Record<string, string> = {
  low: '#22c55e',
  moderate: '#eab308',
  heavy: '#f97316',
  severe: '#ef4444',
  unknown: '#94a3b8',
};

function featureCollection(
  segments: Array<{
    geometry: { coordinates: number[][] };
    congestion: string;
    speed?: number | null;
  }>,
) {
  return {
    type: 'FeatureCollection' as const,
    features: segments.map((seg, idx) => ({
      type: 'Feature' as const,
      id: idx,
      properties: {
        congestion: seg.congestion,
        speed: seg.speed ?? null,
      },
      geometry: {
        type: 'LineString' as const,
        coordinates: seg.geometry.coordinates,
      },
    })),
  };
}

function ensureLiveTrafficLayers(map: MapboxMap, visible: boolean) {
  if (!map.getSource(LIVE_TRAFFIC_SOURCE_ID)) {
    map.addSource(LIVE_TRAFFIC_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }

  if (!map.getLayer(LIVE_TRAFFIC_LAYER_ID)) {
    const beforeId = map.getLayer('ghana-roads-motorway') ? 'ghana-roads-motorway' : undefined;
    map.addLayer(
      {
        id: LIVE_TRAFFIC_LAYER_ID,
        type: 'line',
        source: LIVE_TRAFFIC_SOURCE_ID,
        layout: {
          visibility: visible ? 'visible' : 'none',
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 2, 14, 4, 18, 7],
          'line-color': [
            'match',
            ['get', 'congestion'],
            'low',
            CONGESTION_COLOR.low,
            'moderate',
            CONGESTION_COLOR.moderate,
            'heavy',
            CONGESTION_COLOR.heavy,
            'severe',
            CONGESTION_COLOR.severe,
            CONGESTION_COLOR.unknown,
          ],
          'line-opacity': 0.85,
        },
      },
      beforeId,
    );
    return;
  }

  map.setLayoutProperty(LIVE_TRAFFIC_LAYER_ID, 'visibility', visible ? 'visible' : 'none');
}

async function fetchTrafficForBounds(map: MapboxMap) {
  if (!API_URL) return;
  if (!localStorage.getItem('auth_token')) return;
  const b = map.getBounds();
  if (!b) return;
  const params = new URLSearchParams({
    sw_lat: String(b.getSouth()),
    sw_lng: String(b.getWest()),
    ne_lat: String(b.getNorth()),
    ne_lng: String(b.getEast()),
  });
  const resp = await fetch(`${API_URL}/v1/navigation/traffic?${params}`, {
    headers: getAuthBearerHeaders(),
  });
  if (!resp.ok) return;
  const payload = await resp.json();
  const segments = payload?.data?.segments ?? [];
  const source = map.getSource(LIVE_TRAFFIC_SOURCE_ID) as GeoJSONSource | undefined;
  if (source) {
    source.setData(featureCollection(segments));
  }
}

/** First-party live congestion overlay from /v1/navigation/traffic (Valhalla edge aligned). */
export function useLiveTrafficLayer(
  mapRef: React.MutableRefObject<MapboxMap | null>,
  liveTrafficEnabled: boolean,
) {
  const { isMapLoaded } = useMapStore();
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded) return;

    const refresh = () => {
      if (!liveTrafficEnabled) return;
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        void fetchTrafficForBounds(map).catch(() => {});
      }, 400);
    };

    const apply = () => {
      if (!map.isStyleLoaded()) return;
      try {
        ensureLiveTrafficLayers(map, liveTrafficEnabled);
        if (liveTrafficEnabled) refresh();
      } catch {
        // Style may still be loading.
      }
    };

    apply();
    map.on('style.load', apply);
    map.on('moveend', refresh);
    return () => {
      map.off('style.load', apply);
      map.off('moveend', refresh);
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [mapRef, liveTrafficEnabled, isMapLoaded]);
}
